import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, Settings, Ruler, Package, Trash2, Edit, Eye, FileText, 
  CheckCircle, Loader2, Calculator, DollarSign, Percent, Box,
  Hammer, Wrench, PaintBucket, Truck, Search, RefreshCw, Pencil, Download, Wand2
} from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import Editor2D from './Editor2D';
import ProjetoWizard from './ProjetoWizard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Status colors
const STATUS_COLORS = {
  'rascunho': 'bg-gray-500/10 text-gray-500 border-gray-500',
  'orcamento': 'bg-blue-500/10 text-blue-500 border-blue-500',
  'aprovado': 'bg-green-500/10 text-green-500 border-green-500',
  'em_producao': 'bg-amber-500/10 text-amber-500 border-amber-500',
  'concluido': 'bg-emerald-500/10 text-emerald-600 border-emerald-500',
  'cancelado': 'bg-red-500/10 text-red-500 border-red-500',
};

const STATUS_LABELS = {
  'rascunho': 'Rascunho',
  'orcamento': 'Orçamento',
  'aprovado': 'Aprovado',
  'em_producao': 'Em Produção',
  'concluido': 'Concluído',
  'cancelado': 'Cancelado',
};

export default function ProjetadosTab({ customers = [], onProjetoConverted }) {
  const [projetos, setProjetos] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [projetoModalOpen, setProjetoModalOpen] = useState(false);
  const [wizardModalOpen, setWizardModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingProjeto, setEditingProjeto] = useState(null);
  const [viewingProjeto, setViewingProjeto] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    cliente_id: '',
    ambiente: '',
    tipo_movel: '',
    nome_projeto: '',
    largura: 100,
    altura: 200,
    profundidade: 50,
    material_id: '',
    acabamento_id: '',
    ferragens_selecionadas: [],
    componentes: [],
    margem: 30,
    desconto: 0,
    custo_frete: 0,
    prazo_entrega: 30,
    condicoes_pagamento: '',
    observacoes: '',
  });
  
  // Tab state for project modal
  const [projetoTab, setProjetoTab] = useState('dados');
  
  const [saving, setSaving] = useState(false);
  
  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, projetosRes] = await Promise.all([
        axios.get(`${API}/moveis-projetados/config`),
        axios.get(`${API}/moveis-projetados/projetos`)
      ]);
      setConfig(configRes.data);
      setProjetos(projetosRes.data.projetos || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar projetos');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Filter projects
  const filteredProjetos = projetos.filter(p => {
    const search = searchTerm.toLowerCase();
    return (
      p.cliente_nome?.toLowerCase().includes(search) ||
      p.tipo_movel?.toLowerCase().includes(search) ||
      p.ambiente?.toLowerCase().includes(search) ||
      `PROJ-${String(p.numero_projeto).padStart(4, '0')}`.toLowerCase().includes(search)
    );
  });
  
  // Open new project modal - USANDO WIZARD
  const handleNewProjeto = () => {
    setEditingProjeto(null);
    setWizardModalOpen(true);
  };
  
  // Abrir editor avançado (para edição)
  const handleOpenAdvancedEditor = () => {
    setFormData({
      cliente_id: '',
      ambiente: config?.ambientes?.[0] || '',
      tipo_movel: config?.tipos_moveis?.[0] || '',
      nome_projeto: '',
      largura: 100,
      altura: 200,
      profundidade: 50,
      material_id: config?.materiais?.[0]?.id || '',
      acabamento_id: config?.acabamentos?.[0]?.id || '',
      ferragens_selecionadas: [],
      componentes: [],
      margem: config?.margem_padrao || 30,
      desconto: 0,
      custo_frete: config?.frete_padrao || 0,
      prazo_entrega: 30,
      condicoes_pagamento: '',
      observacoes: '',
    });
    setProjetoTab('dados');
    setProjetoModalOpen(true);
  };
  
  // Callback quando Wizard completa
  const handleWizardComplete = async (projetoData) => {
    setSaving(true);
    try {
      await axios.post(`${API}/moveis-projetados/projetos`, projetoData);
      toast.success('Projeto criado com sucesso!');
      setWizardModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Erro ao criar projeto:', error.response?.data);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Erro de validação do Pydantic
        const mensagens = detail.map(d => d.msg || 'Erro de validação').join(', ');
        toast.error(`Erro de validação: ${mensagens}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Erro ao criar projeto');
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Edit project
  const handleEditProjeto = (projeto) => {
    setEditingProjeto(projeto);
    setFormData({
      cliente_id: projeto.cliente_id || '',
      ambiente: projeto.ambiente || '',
      tipo_movel: projeto.tipo_movel || '',
      nome_projeto: projeto.nome_projeto || '',
      largura: projeto.largura || 100,
      altura: projeto.altura || 200,
      profundidade: projeto.profundidade || 50,
      material_id: projeto.material_id || '',
      acabamento_id: projeto.acabamento_id || '',
      ferragens_selecionadas: projeto.ferragens_selecionadas || [],
      componentes: projeto.componentes || [],
      margem: projeto.margem || 30,
      desconto: projeto.desconto || 0,
      custo_frete: projeto.custo_frete || 0,
      prazo_entrega: projeto.prazo_entrega || 30,
      condicoes_pagamento: projeto.condicoes_pagamento || '',
      observacoes: projeto.observacoes || '',
    });
    setProjetoTab('dados');
    setProjetoModalOpen(true);
  };
  
  // Save project
  const handleSaveProjeto = async () => {
    if (!formData.cliente_id) {
      toast.error('Selecione um cliente');
      return;
    }
    if (!formData.material_id) {
      toast.error('Selecione um material');
      return;
    }
    
    setSaving(true);
    try {
      const cliente = customers.find(c => c.id === formData.cliente_id);
      const payload = {
        ...formData,
        cliente_nome: cliente?.name || '',
      };
      
      if (editingProjeto) {
        await axios.put(`${API}/moveis-projetados/projetos/${editingProjeto.id}`, payload);
        toast.success('Projeto atualizado!');
      } else {
        await axios.post(`${API}/moveis-projetados/projetos`, payload);
        toast.success('Projeto criado!');
      }
      
      setProjetoModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar projeto');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete project
  const handleDeleteProjeto = async (projetoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este projeto?')) return;
    
    try {
      await axios.delete(`${API}/moveis-projetados/projetos/${projetoId}`);
      toast.success('Projeto excluído!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir projeto');
    }
  };
  
  // Approve project (convert to order)
  const handleAprovarProjeto = async (projetoId) => {
    if (!window.confirm('Aprovar projeto e converter em pedido?')) return;
    
    try {
      const res = await axios.post(`${API}/moveis-projetados/projetos/${projetoId}/aprovar`);
      toast.success(`Projeto aprovado! Pedido ${res.data.numero_pedido} criado`);
      fetchData();
      if (onProjetoConverted) onProjetoConverted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar projeto');
    }
  };
  
  // Generate quote
  const handleGerarOrcamento = async (projetoId) => {
    try {
      await axios.post(`${API}/moveis-projetados/projetos/${projetoId}/gerar-orcamento`);
      toast.success('Orçamento gerado!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar orçamento');
    }
  };
  
  // Download PDF
  const handleDownloadPDF = async (projetoId, numeroProjeto) => {
    try {
      toast.info('Gerando PDF...');
      const response = await axios.get(`${API}/moveis-projetados/projetos/${projetoId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orcamento_PROJ-${String(numeroProjeto).padStart(4, '0')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };
  
  // Calculate preview values
  const calcularPreview = () => {
    if (!config) return { area: 0, custoMaterial: 0, custoTotal: 0, valorVenda: 0 };
    
    const largura_m = formData.largura / 100;
    const altura_m = formData.altura / 100;
    const profundidade_m = formData.profundidade / 100;
    
    const area_frontal = largura_m * altura_m;
    const area_laterais = 2 * (profundidade_m * altura_m);
    const area_fundo = largura_m * altura_m;
    const area_tampo = largura_m * profundidade_m;
    const area_total = area_frontal + area_laterais + area_fundo + area_tampo;
    
    const material = config.materiais?.find(m => m.id === formData.material_id);
    const acabamento = config.acabamentos?.find(a => a.id === formData.acabamento_id);
    
    const custoMaterial = area_total * ((material?.preco_m2 || 0) + (acabamento?.preco_m2 || 0));
    
    let custoFerragens = 0;
    formData.ferragens_selecionadas?.forEach(fs => {
      const ferragem = config.ferragens?.find(f => f.id === fs.ferragem_id);
      if (ferragem) custoFerragens += ferragem.preco_unitario * (fs.quantidade || 0);
    });
    
    const custoMaoDeObra = area_total * 8 * config.mao_de_obra_hora;
    const custoInstalacao = area_total * config.instalacao_m2;
    const custoTotal = custoMaterial + custoFerragens + custoMaoDeObra + custoInstalacao + (formData.custo_frete || 0);
    
    const valorVendaBruto = custoTotal * (1 + (formData.margem || 30) / 100);
    const valorVenda = valorVendaBruto - (formData.desconto || 0);
    const lucro = valorVenda - custoTotal;
    
    return {
      area: area_total.toFixed(2),
      custoMaterial: custoMaterial.toFixed(2),
      custoFerragens: custoFerragens.toFixed(2),
      custoMaoDeObra: custoMaoDeObra.toFixed(2),
      custoInstalacao: custoInstalacao.toFixed(2),
      custoTotal: custoTotal.toFixed(2),
      valorVenda: valorVenda.toFixed(2),
      lucro: lucro.toFixed(2),
      margemReal: custoTotal > 0 ? ((lucro / custoTotal) * 100).toFixed(1) : 0,
    };
  };
  
  const preview = calcularPreview();
  
  // Toggle ferragem selection
  const toggleFerragem = (ferragemId) => {
    setFormData(prev => {
      const existing = prev.ferragens_selecionadas.find(f => f.ferragem_id === ferragemId);
      if (existing) {
        return {
          ...prev,
          ferragens_selecionadas: prev.ferragens_selecionadas.filter(f => f.ferragem_id !== ferragemId)
        };
      } else {
        return {
          ...prev,
          ferragens_selecionadas: [...prev.ferragens_selecionadas, { ferragem_id: ferragemId, quantidade: 2 }]
        };
      }
    });
  };
  
  // Update ferragem quantity
  const updateFerragemQuantidade = (ferragemId, quantidade) => {
    setFormData(prev => ({
      ...prev,
      ferragens_selecionadas: prev.ferragens_selecionadas.map(f => 
        f.ferragem_id === ferragemId ? { ...f, quantidade: parseInt(quantidade) || 0 } : f
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <Button onClick={handleNewProjeto} data-testid="btn-novo-projeto">
            <Plus className="h-4 w-4 mr-2" />
            Novo Projeto
          </Button>
          <Button variant="outline" onClick={() => setConfigModalOpen(true)} data-testid="btn-config-projetados">
            <Settings className="h-4 w-4 mr-2" />
            Configurações
          </Button>
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar projetos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-projetos"
            />
          </div>
          <Button variant="ghost" size="icon" onClick={fetchData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Rascunhos</p>
                <p className="text-2xl font-bold">{projetos.filter(p => p.status === 'rascunho').length}</p>
              </div>
              <Box className="h-8 w-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Orçamentos</p>
                <p className="text-2xl font-bold">{projetos.filter(p => p.status === 'orcamento').length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Aprovados</p>
                <p className="text-2xl font-bold">{projetos.filter(p => p.status === 'aprovado').length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Valor Total</p>
                <p className="text-lg font-bold">{formatCurrency(projetos.reduce((sum, p) => sum + (p.valor_venda || 0), 0))}</p>
              </div>
              <DollarSign className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Projects List */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Projetos de Móveis</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredProjetos.length === 0 ? (
            <div className="text-center py-12">
              <Box className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Nenhum projeto encontrado' : 'Nenhum projeto cadastrado'}
              </p>
              <Button onClick={handleNewProjeto}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Projeto
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Projeto</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Móvel</TableHead>
                    <TableHead>Dimensões</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProjetos.map((projeto) => (
                    <TableRow key={projeto.id} data-testid={`projeto-row-${projeto.id}`}>
                      <TableCell className="font-medium">
                        PROJ-{String(projeto.numero_projeto).padStart(4, '0')}
                      </TableCell>
                      <TableCell>{projeto.cliente_nome || '-'}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{projeto.tipo_movel}</p>
                          <p className="text-xs text-muted-foreground">{projeto.ambiente}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {projeto.largura} x {projeto.altura} x {projeto.profundidade} cm
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_COLORS[projeto.status]}>
                          {STATUS_LABELS[projeto.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(projeto.valor_venda || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => { setViewingProjeto(projeto); setViewModalOpen(true); }}
                            title="Visualizar"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {(projeto.status === 'orcamento' || projeto.status === 'aprovado') && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDownloadPDF(projeto.id, projeto.numero_projeto)}
                              title="Baixar PDF"
                              className="text-red-600"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          {projeto.status === 'rascunho' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleEditProjeto(projeto)}
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleGerarOrcamento(projeto.id)}
                                title="Gerar Orçamento"
                              >
                                <FileText className="h-4 w-4 text-blue-500" />
                              </Button>
                            </>
                          )}
                          {projeto.status === 'orcamento' && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleAprovarProjeto(projeto.id)}
                              title="Aprovar"
                              className="text-green-600"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          {!projeto.pedido_id && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteProjeto(projeto.id)}
                              title="Excluir"
                              className="text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* New/Edit Project Modal */}
      <Dialog open={projetoModalOpen} onOpenChange={setProjetoModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Box className="h-5 w-5 text-primary" />
              {editingProjeto ? 'Editar Projeto' : 'Novo Projeto de Móvel'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados e desenhe o móvel para calcular o orçamento automaticamente.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={projetoTab} onValueChange={setProjetoTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="dados" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Dados do Projeto
              </TabsTrigger>
              <TabsTrigger value="desenho" className="flex items-center gap-2">
                <Pencil className="h-4 w-4" />
                Desenho 2D
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="dados">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Form */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Cliente e Ambiente */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cliente *</Label>
                      <Select 
                        value={formData.cliente_id} 
                        onValueChange={(v) => setFormData({...formData, cliente_id: v})}
                      >
                        <SelectTrigger data-testid="select-cliente">
                          <SelectValue placeholder="Selecione o cliente" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Ambiente</Label>
                      <Select 
                        value={formData.ambiente} 
                        onValueChange={(v) => setFormData({...formData, ambiente: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o ambiente" />
                        </SelectTrigger>
                        <SelectContent>
                          {config?.ambientes?.map(amb => (
                            <SelectItem key={amb} value={amb}>{amb}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Tipo de Móvel */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de Móvel</Label>
                      <Select 
                        value={formData.tipo_movel} 
                        onValueChange={(v) => setFormData({...formData, tipo_movel: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {config?.tipos_moveis?.map(tipo => (
                            <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Nome do Projeto (opcional)</Label>
                      <Input
                        value={formData.nome_projeto}
                        onChange={(e) => setFormData({...formData, nome_projeto: e.target.value})}
                        placeholder="Ex: Closet Master Suite"
                      />
                    </div>
                  </div>
                  
                  {/* Dimensões */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Ruler className="h-4 w-4" />
                      Dimensões (cm)
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Largura</Label>
                        <Input
                          type="number"
                          value={formData.largura}
                          onChange={(e) => setFormData({...formData, largura: parseFloat(e.target.value) || 0, componentes: []})}
                          data-testid="input-largura"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Altura</Label>
                        <Input
                          type="number"
                          value={formData.altura}
                          onChange={(e) => setFormData({...formData, altura: parseFloat(e.target.value) || 0, componentes: []})}
                          data-testid="input-altura"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Profundidade</Label>
                        <Input
                          type="number"
                          value={formData.profundidade}
                          onChange={(e) => setFormData({...formData, profundidade: parseFloat(e.target.value) || 0})}
                          data-testid="input-profundidade"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Material e Acabamento */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Material *
                      </Label>
                      <Select 
                        value={formData.material_id} 
                        onValueChange={(v) => setFormData({...formData, material_id: v})}
                      >
                        <SelectTrigger data-testid="select-material">
                          <SelectValue placeholder="Selecione o material" />
                        </SelectTrigger>
                        <SelectContent>
                          {config?.materiais?.filter(m => m.ativo).map(mat => (
                            <SelectItem key={mat.id} value={mat.id}>
                              {mat.nome} - {formatCurrency(mat.preco_m2)}/m²
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <PaintBucket className="h-4 w-4" />
                        Acabamento
                      </Label>
                      <Select 
                        value={formData.acabamento_id} 
                        onValueChange={(v) => setFormData({...formData, acabamento_id: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o acabamento" />
                        </SelectTrigger>
                        <SelectContent>
                          {config?.acabamentos?.filter(a => a.ativo).map(acab => (
                            <SelectItem key={acab.id} value={acab.id}>
                              {acab.nome} - {formatCurrency(acab.preco_m2)}/m²
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Ferragens */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Ferragens
                    </Label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                      {config?.ferragens?.filter(f => f.ativo).map(ferragem => {
                        const selected = formData.ferragens_selecionadas.find(f => f.ferragem_id === ferragem.id);
                        return (
                          <div 
                            key={ferragem.id} 
                            className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                              selected ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                            }`}
                            onClick={() => toggleFerragem(ferragem.id)}
                          >
                            <input
                              type="checkbox"
                              checked={!!selected}
                              onChange={() => toggleFerragem(ferragem.id)}
                              className="h-4 w-4"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{ferragem.nome}</p>
                              <p className="text-xs text-muted-foreground">{formatCurrency(ferragem.preco_unitario)}/un</p>
                            </div>
                            {selected && (
                              <Input
                                type="number"
                                value={selected.quantidade}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  updateFerragemQuantidade(ferragem.id, e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-16 h-7 text-center"
                                min="1"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Margem, Desconto, Frete */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Percent className="h-4 w-4" />
                        Margem (%)
                      </Label>
                      <Input
                        type="number"
                        value={formData.margem}
                        onChange={(e) => setFormData({...formData, margem: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Desconto (R$)</Label>
                      <Input
                        type="number"
                        value={formData.desconto}
                        onChange={(e) => setFormData({...formData, desconto: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Truck className="h-4 w-4" />
                        Frete (R$)
                      </Label>
                      <Input
                        type="number"
                        value={formData.custo_frete}
                        onChange={(e) => setFormData({...formData, custo_frete: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  
                  {/* Prazo e Condições */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prazo de Entrega (dias)</Label>
                      <Input
                        type="number"
                        value={formData.prazo_entrega}
                        onChange={(e) => setFormData({...formData, prazo_entrega: parseInt(e.target.value) || 30})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Condições de Pagamento</Label>
                      <Input
                        value={formData.condicoes_pagamento}
                        onChange={(e) => setFormData({...formData, condicoes_pagamento: e.target.value})}
                        placeholder="Ex: 50% entrada + 50% na entrega"
                      />
                    </div>
                  </div>
                  
                  {/* Observações */}
                  <div className="space-y-2">
                    <Label>Observações</Label>
                    <Textarea
                      value={formData.observacoes}
                      onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                      placeholder="Observações do projeto..."
                      rows={3}
                    />
                  </div>
                </div>
                
                {/* Right Column - Preview */}
                <div className="space-y-4">
                  <Card className="border-primary/20 bg-primary/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Calculator className="h-4 w-4" />
                        Resumo do Orçamento
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Área Total:</span>
                        <span className="font-medium">{preview.area} m²</span>
                      </div>
                      <div className="border-t pt-2 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Material:</span>
                          <span>{formatCurrency(parseFloat(preview.custoMaterial))}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Ferragens:</span>
                          <span>{formatCurrency(parseFloat(preview.custoFerragens))}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Mão de Obra:</span>
                          <span>{formatCurrency(parseFloat(preview.custoMaoDeObra))}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Instalação:</span>
                          <span>{formatCurrency(parseFloat(preview.custoInstalacao))}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Frete:</span>
                          <span>{formatCurrency(formData.custo_frete || 0)}</span>
                        </div>
                      </div>
                      <div className="border-t pt-2">
                        <div className="flex justify-between text-sm font-medium">
                          <span>Custo Total:</span>
                          <span>{formatCurrency(parseFloat(preview.custoTotal))}</span>
                        </div>
                      </div>
                      <div className="border-t pt-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Margem ({formData.margem}%):</span>
                          <span className="text-green-600">+{formatCurrency(parseFloat(preview.valorVenda) - parseFloat(preview.custoTotal) + (formData.desconto || 0))}</span>
                        </div>
                        {formData.desconto > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Desconto:</span>
                            <span className="text-red-500">-{formatCurrency(formData.desconto)}</span>
                          </div>
                        )}
                      </div>
                      <div className="border-t pt-3">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold">Valor de Venda:</span>
                          <span className="text-xl font-bold text-primary">{formatCurrency(parseFloat(preview.valorVenda))}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-muted-foreground">Lucro:</span>
                          <span className={`font-medium ${parseFloat(preview.lucro) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {formatCurrency(parseFloat(preview.lucro))} ({preview.margemReal}%)
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Mini 2D Preview */}
                  <Card className="border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center justify-between">
                        Preview 2D
                        <Badge variant="outline" className="text-xs">
                          {formData.componentes?.length || 0} componentes
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Editor2D
                        largura={formData.largura}
                        altura={formData.altura}
                        profundidade={formData.profundidade}
                        componentes={formData.componentes || []}
                        onComponentesChange={(c) => setFormData({...formData, componentes: c})}
                        readOnly={true}
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-2"
                        onClick={() => setProjetoTab('desenho')}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Editar Desenho
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="desenho">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Editor 2D - Visão Frontal</h3>
                    <p className="text-sm text-muted-foreground">
                      Dimensões: {formData.largura} × {formData.altura} × {formData.profundidade} cm
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setProjetoTab('dados')}>
                    Voltar aos Dados
                  </Button>
                </div>
                
                <Editor2D
                  largura={formData.largura}
                  altura={formData.altura}
                  profundidade={formData.profundidade}
                  componentes={formData.componentes || []}
                  onComponentesChange={(c) => setFormData({...formData, componentes: c})}
                  readOnly={false}
                />
                
                <div className="bg-muted/30 rounded-lg p-3 text-sm text-muted-foreground">
                  <p className="font-medium mb-1">Instruções:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>Selecionar:</strong> Clique no ícone de mão e depois em um componente</li>
                    <li><strong>Adicionar:</strong> Selecione o tipo (porta, gaveta, etc) e clique no móvel</li>
                    <li><strong>Mover:</strong> Arraste o componente selecionado</li>
                    <li><strong>Redimensionar:</strong> Arraste os cantos do componente selecionado</li>
                    <li><strong>Dividir:</strong> Use os botões 2/3/4 para criar portas iguais automaticamente</li>
                  </ul>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProjetoModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveProjeto} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingProjeto ? 'Salvar Alterações' : 'Criar Projeto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Project Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Projeto PROJ-{String(viewingProjeto?.numero_projeto).padStart(4, '0')}
            </DialogTitle>
          </DialogHeader>
          
          {viewingProjeto && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Cliente</p>
                  <p className="font-medium">{viewingProjeto.cliente_nome}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant="outline" className={STATUS_COLORS[viewingProjeto.status]}>
                    {STATUS_LABELS[viewingProjeto.status]}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tipo de Móvel</p>
                  <p className="font-medium">{viewingProjeto.tipo_movel}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Ambiente</p>
                  <p className="font-medium">{viewingProjeto.ambiente}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Dimensões</p>
                  <p className="font-medium">{viewingProjeto.largura} x {viewingProjeto.altura} x {viewingProjeto.profundidade} cm</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Área Total</p>
                  <p className="font-medium">{viewingProjeto.area_total} m²</p>
                </div>
              </div>
              
              <div className="border-t pt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Custo Total</p>
                  <p className="font-medium">{formatCurrency(viewingProjeto.custo_total || 0)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor de Venda</p>
                  <p className="text-xl font-bold text-primary">{formatCurrency(viewingProjeto.valor_venda || 0)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Lucro</p>
                  <p className={`font-medium ${(viewingProjeto.lucro || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {formatCurrency(viewingProjeto.lucro || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Prazo de Entrega</p>
                  <p className="font-medium">{viewingProjeto.prazo_entrega} dias</p>
                </div>
              </div>
              
              {viewingProjeto.observacoes && (
                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground">Observações</p>
                  <p className="text-sm">{viewingProjeto.observacoes}</p>
                </div>
              )}
              
              {viewingProjeto.pedido_id && (
                <div className="border-t pt-4">
                  <Badge className="bg-green-500">Convertido em Pedido</Badge>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewModalOpen(false)}>
              Fechar
            </Button>
            {(viewingProjeto?.status === 'orcamento' || viewingProjeto?.status === 'aprovado') && (
              <Button 
                variant="outline"
                onClick={() => handleDownloadPDF(viewingProjeto.id, viewingProjeto.numero_projeto)}
                className="text-red-600 border-red-200"
              >
                <Download className="h-4 w-4 mr-2" />
                Baixar PDF
              </Button>
            )}
            {viewingProjeto?.status === 'rascunho' && (
              <Button onClick={() => { setViewModalOpen(false); handleEditProjeto(viewingProjeto); }}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            )}
            {viewingProjeto?.status === 'orcamento' && !viewingProjeto?.pedido_id && (
              <Button onClick={() => { setViewModalOpen(false); handleAprovarProjeto(viewingProjeto.id); }} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Aprovar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Config Modal */}
      <ConfigModal 
        open={configModalOpen} 
        onOpenChange={setConfigModalOpen}
        config={config}
        onSave={(newConfig) => { setConfig(newConfig); fetchData(); }}
      />
      
      {/* WIZARD Modal - Novo Projeto Simplificado */}
      <Dialog open={wizardModalOpen} onOpenChange={setWizardModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-primary" />
              Assistente de Projeto
            </DialogTitle>
            <DialogDescription>
              Responda as perguntas e o sistema criará o projeto automaticamente.
            </DialogDescription>
          </DialogHeader>
          
          <ProjetoWizard 
            config={config}
            customers={customers}
            onComplete={handleWizardComplete}
            onCancel={() => setWizardModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Config Modal Component
function ConfigModal({ open, onOpenChange, config, onSave }) {
  const [localConfig, setLocalConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('materiais');
  
  useEffect(() => {
    if (config) {
      setLocalConfig({ ...config });
    }
  }, [config]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/moveis-projetados/config`, localConfig);
      toast.success('Configurações salvas!');
      onSave(res.data.config);
      onOpenChange(false);
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };
  
  const addMaterial = () => {
    setLocalConfig(prev => ({
      ...prev,
      materiais: [...(prev.materiais || []), { id: crypto.randomUUID(), nome: '', preco_m2: 0, descricao: '', ativo: true }]
    }));
  };
  
  const addAcabamento = () => {
    setLocalConfig(prev => ({
      ...prev,
      acabamentos: [...(prev.acabamentos || []), { id: crypto.randomUUID(), nome: '', preco_m2: 0, descricao: '', ativo: true }]
    }));
  };
  
  const addFerragem = () => {
    setLocalConfig(prev => ({
      ...prev,
      ferragens: [...(prev.ferragens || []), { id: crypto.randomUUID(), nome: '', tipo: 'outro', preco_unitario: 0, descricao: '', ativo: true }]
    }));
  };
  
  const updateMaterial = (idx, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      materiais: prev.materiais.map((m, i) => i === idx ? { ...m, [field]: value } : m)
    }));
  };
  
  const updateAcabamento = (idx, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      acabamentos: prev.acabamentos.map((a, i) => i === idx ? { ...a, [field]: value } : a)
    }));
  };
  
  const updateFerragem = (idx, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      ferragens: prev.ferragens.map((f, i) => i === idx ? { ...f, [field]: value } : f)
    }));
  };
  
  const removeMaterial = (idx) => {
    setLocalConfig(prev => ({
      ...prev,
      materiais: prev.materiais.filter((_, i) => i !== idx)
    }));
  };
  
  const removeAcabamento = (idx) => {
    setLocalConfig(prev => ({
      ...prev,
      acabamentos: prev.acabamentos.filter((_, i) => i !== idx)
    }));
  };
  
  const removeFerragem = (idx) => {
    setLocalConfig(prev => ({
      ...prev,
      ferragens: prev.ferragens.filter((_, i) => i !== idx)
    }));
  };
  
  if (!localConfig) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configurações de Móveis Projetados
          </DialogTitle>
          <DialogDescription>
            Configure materiais, acabamentos, ferragens e valores padrão.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="materiais">Materiais</TabsTrigger>
            <TabsTrigger value="acabamentos">Acabamentos</TabsTrigger>
            <TabsTrigger value="ferragens">Ferragens</TabsTrigger>
            <TabsTrigger value="valores">Valores</TabsTrigger>
          </TabsList>
          
          <TabsContent value="materiais" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <Label>Materiais Cadastrados</Label>
              <Button size="sm" onClick={addMaterial}>
                <Plus className="h-4 w-4 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {localConfig.materiais?.map((mat, idx) => (
                <div key={mat.id} className="flex items-center gap-2 p-2 border rounded">
                  <Input
                    value={mat.nome}
                    onChange={(e) => updateMaterial(idx, 'nome', e.target.value)}
                    placeholder="Nome"
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={mat.preco_m2}
                    onChange={(e) => updateMaterial(idx, 'preco_m2', parseFloat(e.target.value) || 0)}
                    placeholder="R$/m²"
                    className="w-24"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeMaterial(idx)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="acabamentos" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <Label>Acabamentos Cadastrados</Label>
              <Button size="sm" onClick={addAcabamento}>
                <Plus className="h-4 w-4 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {localConfig.acabamentos?.map((acab, idx) => (
                <div key={acab.id} className="flex items-center gap-2 p-2 border rounded">
                  <Input
                    value={acab.nome}
                    onChange={(e) => updateAcabamento(idx, 'nome', e.target.value)}
                    placeholder="Nome"
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={acab.preco_m2}
                    onChange={(e) => updateAcabamento(idx, 'preco_m2', parseFloat(e.target.value) || 0)}
                    placeholder="R$/m²"
                    className="w-24"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeAcabamento(idx)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="ferragens" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <Label>Ferragens Cadastradas</Label>
              <Button size="sm" onClick={addFerragem}>
                <Plus className="h-4 w-4 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {localConfig.ferragens?.map((ferr, idx) => (
                <div key={ferr.id} className="flex items-center gap-2 p-2 border rounded">
                  <Input
                    value={ferr.nome}
                    onChange={(e) => updateFerragem(idx, 'nome', e.target.value)}
                    placeholder="Nome"
                    className="flex-1"
                  />
                  <Select 
                    value={ferr.tipo} 
                    onValueChange={(v) => updateFerragem(idx, 'tipo', v)}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dobradiça">Dobradiça</SelectItem>
                      <SelectItem value="corrediça">Corrediça</SelectItem>
                      <SelectItem value="puxador">Puxador</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    value={ferr.preco_unitario}
                    onChange={(e) => updateFerragem(idx, 'preco_unitario', parseFloat(e.target.value) || 0)}
                    placeholder="R$/un"
                    className="w-24"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeFerragem(idx)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="valores" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mão de Obra (R$/hora)</Label>
                <Input
                  type="number"
                  value={localConfig.mao_de_obra_hora}
                  onChange={(e) => setLocalConfig({...localConfig, mao_de_obra_hora: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Margem Padrão (%)</Label>
                <Input
                  type="number"
                  value={localConfig.margem_padrao}
                  onChange={(e) => setLocalConfig({...localConfig, margem_padrao: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Instalação (R$/m²)</Label>
                <Input
                  type="number"
                  value={localConfig.instalacao_m2}
                  onChange={(e) => setLocalConfig({...localConfig, instalacao_m2: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Frete Padrão (R$)</Label>
                <Input
                  type="number"
                  value={localConfig.frete_padrao}
                  onChange={(e) => setLocalConfig({...localConfig, frete_padrao: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Salvar Configurações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
